import { useMemo } from 'react';
import { ShipInfo } from '@ais-ui/websockets';
import { useShipPortCallsHistory, useServiceAvailability } from '@ais-ui/api';
import { Ship } from '@ais-ui/api-spec/portserver';
import { LastPortCallData } from './LastPortCall';

/**
 * Fetches the most recent port call from portserver (same source as vessel timeline).
 * Uses the ServiceAvailability pattern to gracefully handle portserver being unavailable.
 */
export function useLastPortCall(shipInfo: ShipInfo) {
  const { isAvailable } = useServiceAvailability();
  const portserverAvailable = isAvailable('portserver');

  const imoNumber = shipInfo.imo ? parseInt(shipInfo.imo, 10) : undefined;
  const shipData: Ship | undefined =
    imoNumber && !isNaN(imoNumber) ? { imo: imoNumber } : undefined;

  const enabled = portserverAvailable && !!shipData;

  const { data, isLoading } = useShipPortCallsHistory(
    enabled ? shipData : undefined,
  );

  const lastPortCallData: LastPortCallData | null = useMemo(() => {
    const records = data?.records;
    if (!records || records.length === 0) return null;

    // Find the most recent port call by latest arrival or departure time
    let mostRecent = records[0];
    for (const record of records) {
      const recordTime = record.atd ?? record.ata;
      const currentTime = mostRecent.atd ?? mostRecent.ata;
      if (
        recordTime &&
        currentTime &&
        recordTime.getTime() > currentTime.getTime()
      ) {
        mostRecent = record;
      }
    }

    if (!mostRecent.port?.name || !mostRecent.port?.locode) return null;

    return {
      portName: mostRecent.port.name,
      locode: mostRecent.port.locode,
      country: mostRecent.port.country,
      arrivalTime: mostRecent.ata ?? undefined,
      departureTime: mostRecent.atd ?? undefined,
    };
  }, [data]);

  return {
    data: lastPortCallData,
    isLoading: enabled && isLoading,
    showLastPortCall: portserverAvailable && !!imoNumber,
  };
}
